import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { authClient, type TokenPair } from '../services/authClient'
import { tokenStore, type StoredUser } from './tokenStore'

type AuthState =
  | { status: 'loading' }
  | { status: 'authenticated'; user: StoredUser }
  | { status: 'guest' }

type AuthContextValue = {
  state: AuthState
  login: (email: string, password: string) => Promise<StoredUser>
  loginAdmin: (email: string, password: string) => Promise<StoredUser>
  acceptTokenPair: (pair: TokenPair) => void
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() =>
    tokenStore.isAuthenticated() ? { status: 'loading' } : { status: 'guest' },
  )

  // Si hay sesión persistida, revalidar contra /me al montar.
  useEffect(() => {
    if (!tokenStore.isAuthenticated()) return
    let cancelled = false

    authClient
      .me()
      .then((res) => {
        if (cancelled) return
        tokenStore.setUser(res.user)
        setState({ status: 'authenticated', user: res.user })
      })
      .catch(() => {
        if (cancelled) return
        tokenStore.clear()
        setState({ status: 'guest' })
      })

    return () => {
      cancelled = true
    }
  }, [])

  // Escuchar el evento de logout que dispara apiClient cuando el refresh falla.
  useEffect(() => {
    function handle() {
      setState({ status: 'guest' })
    }
    window.addEventListener('auth:logout', handle)
    return () => window.removeEventListener('auth:logout', handle)
  }, [])

  const acceptTokenPair = useCallback((pair: TokenPair) => {
    tokenStore.setSession(pair.access_token, pair.refresh_token, pair.user)
    setState({ status: 'authenticated', user: pair.user })
  }, [])

  const login = useCallback(
    async (email: string, password: string) => {
      const pair = await authClient.login({ email, password })
      acceptTokenPair(pair)
      return pair.user
    },
    [acceptTokenPair],
  )

  const loginAdmin = useCallback(
    async (email: string, password: string) => {
      const pair = await authClient.loginAdmin({ email, password })
      acceptTokenPair(pair)
      return pair.user
    },
    [acceptTokenPair],
  )

  const logout = useCallback(async () => {
    try {
      await authClient.logout()
    } catch {
      // No bloquear el logout si el servidor falla.
    }
    tokenStore.clear()
    setState({ status: 'guest' })
  }, [])

  const refreshUser = useCallback(async () => {
    const res = await authClient.me()
    tokenStore.setUser(res.user)
    setState({ status: 'authenticated', user: res.user })
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({ state, login, loginAdmin, acceptTokenPair, logout, refreshUser }),
    [state, login, loginAdmin, acceptTokenPair, logout, refreshUser],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth debe usarse dentro de <AuthProvider>.')
  }
  return ctx
}
